import React from "react";
import Button from "@mui/material/Button";

import {
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { UserResource } from "../../../../api/users";
import { useFormik } from "formik";

interface MigrateEmailFormProperties {
  userId: string;
  userEmail?: string;
  onClose: () => void;
  onSuccess: () => void;
}

/// A form component to migrate a user's email address
export const MigrateEmailForm: React.FC<MigrateEmailFormProperties> = ({
  userId,
  userEmail,
  onClose,
  onSuccess,
}) => {
  function handleClose() {
    formik.resetForm();
    onClose();
  }

  const formik = useFormik({
    initialValues: { email: "" },
    onSubmit: async ({ email }) => {
      try {
        await UserResource.migrateEmail(userId, email.trim());
        onSuccess();
        handleClose();
      } catch (e) {
        console.error("Error updating user email: ", e);
        formik.setErrors({
          email: `Could not update user email. Error: ${e}`,
        });
      }
    },
    validate: ({ email }) => {
      const errors: Record<string, string> = {};
      if (!email) {
        errors.email = "Email is required";
      } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
        errors.email = "Invalid email format";
      }
      if (email.trim() === userEmail?.trim()) {
        errors.email = "New email must be different from the current email";
      }
    },
  });

  return (
    <>
      <DialogTitle>
        Update user's email
        <Typography variant="subtitle1" color="textSecondary">
          Current email: <strong>{userEmail || "Not provided"}</strong>
        </Typography>
      </DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            padding: "1rem",
          }}
        >
          <TextField
            id="email"
            name="email"
            label="New Email"
            value={formik.values.email}
            onChange={formik.handleChange}
            required
            fullWidth
            error={Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </form>
    </>
  );
};
